/**
 * SlabReality Home Page
 * =====================
 * FULLY DYNAMIC - All content from Blueprint (Supabase)
 * 
 * The frontend is a PURE RENDERER.
 * NO hardcoded text. NO hardcoded sections. NO fallback.
 * Everything comes from Blueprint:
 * - sections → config.ui.home.sections
 * - order → derived from sections[key].order
 * - content → config.ui.home.{hero, discovery, stats, cta}
 * 
 * DATA SOURCE: SUPABASE ONLY
 */

import React, { useMemo } from 'react';
import { useBlueprint } from '../context/BlueprintContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Import all home components
import {
  HeroSRE,
  DiscoveryCards,
  StatsBar,
  EcosystemSection,
  WorkspacePreview,
  FeaturedMaterials,
  NetworkSection,
  CTASection
} from '../components/home';

// Import styles
import '../styles/slabreality.css';

// Section component mapping (key -> Component)
const SECTION_COMPONENTS = {
  hero: HeroSRE,
  discovery: DiscoveryCards,
  stats: StatsBar,
  ecosystem: EcosystemSection,
  workspace: WorkspacePreview,
  materials: FeaturedMaterials,
  network: NetworkSection,
  cta: CTASection
};

const HomePage = () => {
  const { loading, error, homepage, getUI } = useBlueprint();
  
  // Get sections config from Blueprint
  const sectionsConfig = useMemo(() => {
    return getUI('home.sections', {});
  }, [getUI]);
  
  // Build ordered list of visible sections
  const visibleSections = useMemo(() => {
    // If sectionsConfig is empty, show all sections in default order
    if (!sectionsConfig || Object.keys(sectionsConfig).length === 0) {
      return Object.keys(SECTION_COMPONENTS);
    }
    
    // Build array of { key, order, enabled }
    const sectionList = Object.keys(SECTION_COMPONENTS).map(key => {
      const config = sectionsConfig[key];
      // Handle both object format { enabled, order } and simple boolean
      if (typeof config === 'object') {
        return {
          key,
          order: config.order ?? 99,
          enabled: config.enabled !== false
        };
      }
      // Handle array format from old config
      if (Array.isArray(sectionsConfig)) {
        const item = sectionsConfig.find(s => s.id === key || s.key === key);
        return {
          key,
          order: item?.order ?? 99,
          enabled: item?.enabled !== false
        };
      }
      // Simple boolean or undefined
      return {
        key,
        order: 99,
        enabled: config !== false
      };
    });
    
    // Filter enabled, sort by order
    return sectionList
      .filter(s => s.enabled)
      .sort((a, b) => a.order - b.order)
      .map(s => s.key);
  }, [sectionsConfig]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-[2px] border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--t2)] font-[var(--ff-b)]">Caricamento...</p>
        </div>
      </div>
    );
  }
  
  // Error state - still show page but with error banner
  if (error) {
    console.error('HomePage error:', error);
    // Don't block - try to render anyway
  }
  
  return (
    <div className="min-h-screen bg-[var(--cream)]">
      {/* Navbar - always shown */}
      <Header />
      
      {/* Error banner if any */}
      {error && (
        <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-2 text-center">
          <p className="text-sm text-red-600">
            Errore caricamento configurazione: {error}
          </p>
        </div>
      )}
      
      {/* Dynamic sections - rendered in order from Blueprint */}
      {visibleSections.map(sectionKey => {
        const SectionComponent = SECTION_COMPONENTS[sectionKey];
        if (!SectionComponent) return null;
        return <SectionComponent key={sectionKey} />;
      })}
      
      {/* Footer - always shown */}
      <Footer />
    </div>
  );
};

export default HomePage;
